import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Flex, theme, Select, Spin, message } from 'antd';
import { Queue } from '../../../../../../types/queue';
import { HttpRequest } from '../../../../../../utils/httpClient';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Title, Text } = Typography;
const { useToken } = theme;
const { Option } = Select;

interface QueueSelectorProps {
  chatbotId: string;
  accountId?: string;
  onCancel: () => void;
  onConfirm: (selectedQueue: Queue) => void;
}

export const QueueSelector: React.FC<QueueSelectorProps> = ({
  chatbotId,
  accountId,
  onCancel,
  onConfirm,
}) => {
  const { token } = useToken();
  const [queues, setQueues] = useState<Queue[]>([]);
  const [selectedQueueId, setSelectedQueueId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetchQueues();
  }, [chatbotId]);

  const fetchQueues = async () => {
    try {
      if (!accountId) {
        setError('Account ID is required');
        return;
      }

      setLoading(true);
      // Usar el endpoint de producción de QueueManagement
      const request = new HttpRequest(`${process.env.REACT_APP_API_HOST || 'https://api.dev.mind.creai.cloud'}/queue-management/queues`);
      const response = await request.get(`?chatbotId=${chatbotId}&accountId=${accountId}&status=active`);

      logger.info('QueueManagement API Response:', response);

      // Acceder a response.data para obtener los datos de la respuesta
      const responseData = response.data;
      logger.info('Response data:', responseData);

      // La lambda devuelve los datos en una estructura anidada: responseData.response.queues
      if (responseData && responseData.response && responseData.response.queues && Array.isArray(responseData.response.queues)) {
        // Estructura correcta de la lambda: { message, response: { success, queues, totalCount } }
        setQueues(responseData.response.queues);
      } else if (responseData && Array.isArray(responseData)) {
        // Si la respuesta es directamente un array de colas (fallback)
        setQueues(responseData);
      } else if (responseData && responseData.queues && Array.isArray(responseData.queues)) {
        // Si la respuesta tiene una estructura con 'queues' (fallback)
        setQueues(responseData.queues);
      } else {
        logger.error('Unexpected response structure:', responseData);
        setError('Could not load queues - unexpected response structure');
      }
    } catch (err) {
      logger.error('Error fetching queues:', err);
      setError('Error loading queues');
      message.error('Error loading queues');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (!selectedQueueId) {
      message.warning('Please select a queue');
      return;
    }

    const selectedQueue = queues.find(queue => queue.id === selectedQueueId);
    if (selectedQueue) {
      onConfirm(selectedQueue);
    }
  };

  const selectedQueue = queues.find(queue => queue.id === selectedQueueId);

  if (loading) {
    return (
      <Card style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', border: 'none' }}>
        <Flex vertical gap={token.marginLG} align="center">
          <Spin size="large" />
          <Text>Loading available queues...</Text>
        </Flex>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', border: 'none' }}>
        <Flex vertical gap={token.marginLG} align="center">
          <Text type="danger">{error}</Text>
          <Button onClick={fetchQueues}>Retry</Button>
        </Flex>
      </Card>
    );
  }

  if (queues.length === 0) {
    return (
      <Card style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', border: 'none' }}>
        <Flex vertical gap={token.marginLG} align="center">
          <Title level={3} style={{ margin: 0 }}>
            No queues available
          </Title>
          <Text
            style={{
              fontSize: token.fontSizeLG,
              color: token.colorTextSecondary,
              maxWidth: 450,
            }}
          >
            No active queues were found for this chatbot.
            You must first create a queue in the Queue Management section.
          </Text>
          <Button onClick={onCancel}>
            Cancel
          </Button>
        </Flex>
      </Card>
    );
  }

  return (
    <Card style={{ maxWidth: 600, margin: '0 auto', textAlign: 'center', border: 'none' }}>
      <Flex vertical gap={token.marginLG} align="center">
        {/* Icono de cola */}
        <div
          style={{
            width: 64,
            height: 64,
            borderRadius: '50%',
            backgroundColor: token.colorPrimary,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: 24,
            fontWeight: 'bold',
          }}
        >
          Q
        </div>

        <Title level={3} style={{ margin: 0 }}>
          Select Queue
        </Title>

        <Text
          style={{
            fontSize: token.fontSizeLG,
            color: token.colorTextSecondary,
            maxWidth: 450,
          }}
        >
          Select the queue to which you want to assign this conversation.
          Only active queues are shown.
        </Text>

        <div style={{ width: '100%', maxWidth: 400 }}>
          <Select
            placeholder="Select a queue"
            style={{ width: '100%' }}
            value={selectedQueueId}
            onChange={setSelectedQueueId}
            size="large"
          >
            {queues.map((queue) => (
              <Option key={queue.id} value={queue.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{queue.name}</span>
                  <span style={{ fontSize: '12px', color: token.colorTextSecondary }}>
                    Priority: {queue.priority}
                  </span>
                </div>
              </Option>
            ))}
          </Select>

          {selectedQueue && (
            <div style={{
              marginTop: token.marginMD,
              padding: token.paddingSM,
              backgroundColor: token.colorBgContainer,
              borderRadius: token.borderRadius,
              border: `1px solid ${token.colorBorder}`
            }}>
              <Text strong>Selected queue:</Text>
              <div style={{ marginTop: token.marginXS }}>
                <Text>{selectedQueue.name}</Text>
                {selectedQueue.description && (
                  <div>
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {selectedQueue.description}
                    </Text>
                  </div>
                )}
                <div style={{ marginTop: token.marginXS }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Maximum wait time: {selectedQueue.maxWaitTime} minutes
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>

        <Flex gap={token.marginMD} style={{ width: '100%' }}>
          <Button size="large" style={{ flex: 1 }} onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="large"
            style={{ flex: 1 }}
            onClick={handleConfirm}
            disabled={!selectedQueueId}
          >
            Assign to Queue
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};
